import { Injectable, Injector } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { NotifyService } from '@neutrino/core/notify';
import { filter, Observable } from 'rxjs';
import logger from 'loglevel';

import { AnySupportRequestUpdate, SruKey, SupportRequestUpdate } from '@flb/common/support-request';

import {
    SupportRequestNotificationComponent,
    SupportRequestNotificationParams
} from './support-request-notification.component.js';

const SSRN_KEY = 'skipSupportRequestNotifications';
const SUPPORT_REQUEST_UPDATE_NOTIFICATION_DURATION_MS = 30 * 1000;

@Injectable({
    providedIn: 'root'
})
export class SupportRequestNotificationService {
    constructor(
        private injector: Injector,
        private notify: NotifyService
    ) {}

    supportRequestUpdateToString(sru: AnySupportRequestUpdate): string {
        const summary = 'summary' in sru ? [`\t${sru.summary}`] : summarize(sru);
        return [
            `${sru.author} ${'commentId' in sru ? 'comment added to' : 'updated'} "${sru.title}"${summary.length ? ':' : ''}`,
            ...summary
        ].join('\n');

        function summarize(sru: SupportRequestUpdate): string[] {
            if (sru.updatedFields) {
                return Object.entries(sru.updatedFields).map(([k, [oldValue, newValue]]) => {
                    const desc = oldValue || newValue ? `from ${oldValue} to ${newValue}` : '';
                    return `\t${k} updated ${desc}`;
                });
            }
            return [];
        }
    }

    setupNotifications(supportRequests: Observable<AnySupportRequestUpdate>) {
        supportRequests
            .pipe(filter(sru => !this.shouldSkipSupportRequestNotification(sru)))
            .forEach(sru => {
                const params = new SupportRequestNotificationParams(sru);
                const cp = new ComponentPortal(
                    SupportRequestNotificationComponent,
                    null,
                    Injector.create({
                        providers: [
                            { provide: SupportRequestNotificationParams, useValue: params }
                        ],
                        parent: this.injector
                    })
                );
                this.notify.info(cp, { duration: SUPPORT_REQUEST_UPDATE_NOTIFICATION_DURATION_MS });
            })
            .catch(ex => logger.error('srNotifications', ex));
    }

    skipSupportRequestNotification(sru: SruKey) {
        const keys = sessionStorage.getItem(SSRN_KEY)?.split(',').slice(0, 10) || [];
        const value = [...keys, this.makeSruKey(sru)].join(',');
        sessionStorage.setItem(SSRN_KEY, value);
    }

    shouldSkipSupportRequestNotification(sru: AnySupportRequestUpdate): boolean {
        const skipKeys = sessionStorage.getItem(SSRN_KEY)?.split(',') || [];
        return skipKeys.includes(this.makeSruKey(sru));
    }

    private makeSruKey(sru: SruKey) {
        return `${sru.commentId || sru.id}:${sru.updated}`;
    }
}
